import { GraphNodeProcessingState, ProcessType, TableType, WorkStepType, } from "Qrc/lib/generated/enum";
import { isAttachment, isLaserSheetCuttingGasUniqueMembers, isPackagingUniqueMembers, isPoint3, isScrewThreadUniqueMembers, isSheetMaterialUniqueMembers, isStringIndexedInterface, isTubeMaterialUniqueMembers, isTubeSpecificationUniqueMembers, isUserDefinedScalePrice, } from "Qrc/lib/generated/typeguard";
import { isBendLineEngravingMode, } from "./bend_line_engraving_mode";
import { getSettingOrDefault, } from "./settings_table";
import { getTable, } from "./table_utils";
import { exhaustiveStringTuple, getKeysOfObject, isArray, isBoolean, isNumber, isString, } from "./utils";
export function isTappingDataEntry(arg) {
    const typeGuardMap = {
        center3: isPoint3,
        screwThread: isScrewThreadUniqueMembers,
    };
    return isStringIndexedInterface(arg) && getKeysOfObject(typeGuardMap)
        .every(key => typeGuardMap[key](arg[key]));
}
const articleUserDataTypeGuardMap = {
    name: isString,
    comment: isString,
    externalPartNumber: isString,
    externalDrawingNumber: isString,
    externalRevisionNumber: isString,
};
export function isArticleUserDataEntry(key, value) {
    return articleUserDataTypeGuardMap[key](value);
}
function isArticleUserData(arg) {
    return isStringIndexedInterface(arg) && getKeysOfObject(articleUserDataTypeGuardMap)
        .every(key => articleUserDataTypeGuardMap[key](arg[key]));
}
function isOptionalNumber(arg) {
    return arg === undefined || isNumber(arg);
}
const nodeTypeGuardMap = {
    sheetMaterial: isSheetMaterialUniqueMembers,
    comment: isString,
    materialCostsPerPiece: isOptionalNumber,
    setupTime: isOptionalNumber,
    unitTimePerPiece: isOptionalNumber,
    cuttingGas: isLaserSheetCuttingGasUniqueMembers,
    packaging: isPackagingUniqueMembers,
    transportDistance: isNumber,
    importId: (arg) => (arg === undefined || isString(arg)),
    attachments: (arg) => isArray(arg, isAttachment),
    testReportRequired: isBoolean,
    fixedRotations: (arg) => isArray(arg, isNumber),
    deburrDoubleSided: isBoolean,
    numThreads: isNumber,
    numCountersinks: isNumber,
    sheetTappingData: (arg) => isArray(arg, isTappingDataEntry),
    articleUserData: isArticleUserData,
    userDefinedScalePrices: (arg) => isArray(arg, isUserDefinedScalePrice),
    tubeMaterial: (arg) => arg === undefined || isTubeMaterialUniqueMembers(arg),
    tubeSpecification: (arg) => arg === undefined || isTubeSpecificationUniqueMembers(arg),
    sheetFilterSheetIds: (arg) => isArray(arg, isString),
    bendLineEngravingMode: isBendLineEngravingMode,
};
const createNodeUserDataStringIndexFunctionMap = {
    sheetMaterial: () => "material",
    comment: () => "comment",
    materialCostsPerPiece: context => (context.workStepType === WorkStepType.userDefinedBase ? "price" : "materialCostsPerPiece"),
    setupTime: () => "setupTime",
    unitTimePerPiece: () => "unitTimePerPiece",
    cuttingGas: () => "laserCuttingGas",
    packaging: () => "packaging",
    transportDistance: () => "km",
    importId: () => "importId",
    attachments: () => "attachments",
    testReportRequired: () => "testReportRequired",
    fixedRotations: () => "fixedRotations",
    deburrDoubleSided: () => "deburrDoubleSided",
    numThreads: () => "numThreads",
    numCountersinks: () => "numCountersinks",
    sheetTappingData: () => "sheetTappingData",
    articleUserData: () => "articleUserData",
    userDefinedScalePrices: () => "userDefinedScalePrices",
    tubeMaterial: () => "tubeMaterial",
    tubeSpecification: () => "tubeSpecification",
    sheetFilterSheetIds: () => "sheetFilterSheetIds",
    bendLineEngravingMode: () => "bendLineEngravingMode",
};
function isSheetMaterialAndTestReportWst(wst) {
    return wst === WorkStepType.sheetCutting || wst === WorkStepType.sheetBending;
}
const noCommentWsts = [WorkStepType.sheet];
const nodeCompatibilityFunctionMap = {
    sheetMaterial: context => context.isInitialNode && isSheetMaterialAndTestReportWst(context.workStepType),
    testReportRequired: context => context.isInitialNode && isSheetMaterialAndTestReportWst(context.workStepType),
    comment: context => noCommentWsts.every(wst => wst !== context.workStepType),
    materialCostsPerPiece: () => true,
    setupTime: () => true,
    unitTimePerPiece: () => true,
    cuttingGas: context => context.processType === ProcessType.laserSheetCutting,
    packaging: context => context.workStepType === WorkStepType.packaging,
    transportDistance: context => context.processType === ProcessType.transport,
    importId: () => true,
    attachments: () => true,
    fixedRotations: context => context.workStepType === WorkStepType.sheetCutting && getSettingOrDefault("sheetCuttingFixedRotationsEnabled"),
    deburrDoubleSided: context => context.processType === ProcessType.automaticMechanicalDeburring || context.processType === ProcessType.manualMechanicalDeburring,
    numThreads: context => context.processType === ProcessType.userDefinedThreading,
    numCountersinks: context => context.processType === ProcessType.userDefinedCountersinking,
    sheetTappingData: context => context.processType === ProcessType.sheetTapping,
    articleUserData: context => context.isSignatureNode,
    userDefinedScalePrices: () => true,
    tubeMaterial: context => context.isInitialNode && context.workStepType === WorkStepType.tubeCutting,
    tubeSpecification: context => context.isInitialNode && context.workStepType === WorkStepType.tubeCutting,
    sheetFilterSheetIds: context => context.workStepType === WorkStepType.sheetCutting,
    bendLineEngravingMode: context => context.workStepType === WorkStepType.sheetCutting || context.workStepType === WorkStepType.sheetBending,
};
export function createNodeUserDataStringIndex(key, context) {
    return createNodeUserDataStringIndexFunctionMap[key](context);
}
export function nodeUserDataEntries() {
    return exhaustiveStringTuple()("sheetMaterial", "comment", "materialCostsPerPiece", "setupTime", "unitTimePerPiece", "cuttingGas", "packaging", "transportDistance", "importId", "attachments", "testReportRequired", "fixedRotations", "deburrDoubleSided", "numThreads", "numCountersinks", "sheetTappingData", "articleUserData", "userDefinedScalePrices", "tubeMaterial", "tubeSpecification", "sheetFilterSheetIds", "bendLineEngravingMode");
}
export function isNodeUserDataEntry(arg) {
    return nodeUserDataEntries()
        .some(key => key === arg);
}
export function isNodeUserDataEntryType(key, value) {
    return nodeTypeGuardMap[key](value);
}
export function isCompatibleToNodeUserDataEntryImpl(key, context) {
    return nodeCompatibilityFunctionMap[key](context);
}
export const nodeUserDataProcessingStateMap = {
    sheetMaterial: GraphNodeProcessingState.UpdateWorkStep,
    comment: GraphNodeProcessingState.Finished,
    materialCostsPerPiece: GraphNodeProcessingState.Finished,
    setupTime: GraphNodeProcessingState.Finished,
    unitTimePerPiece: GraphNodeProcessingState.Finished,
    cuttingGas: GraphNodeProcessingState.CreateSources,
    packaging: GraphNodeProcessingState.FillFromSources,
    transportDistance: GraphNodeProcessingState.Finished,
    importId: GraphNodeProcessingState.Finished,
    attachments: GraphNodeProcessingState.Finished,
    testReportRequired: GraphNodeProcessingState.Finished,
    fixedRotations: GraphNodeProcessingState.CreateSources,
    deburrDoubleSided: GraphNodeProcessingState.Finished,
    numThreads: GraphNodeProcessingState.Finished,
    numCountersinks: GraphNodeProcessingState.Finished,
    sheetTappingData: GraphNodeProcessingState.Finished,
    articleUserData: GraphNodeProcessingState.Finished,
    userDefinedScalePrices: GraphNodeProcessingState.Finished,
    tubeMaterial: GraphNodeProcessingState.UpdateWorkStep,
    tubeSpecification: GraphNodeProcessingState.UpdateWorkStep,
    sheetFilterSheetIds: GraphNodeProcessingState.CreateSources,
    bendLineEngravingMode: GraphNodeProcessingState.Finished,
};
const graphTypeGuardMap = {
    attachments: (arg) => isArray(arg, isAttachment),
    name: (arg) => isString(arg),
};
const graphUserDataKeyMap = {
    attachments: "attachments",
    name: "name",
};
function isGraphUserDataEntryType(key, arg) {
    return graphTypeGuardMap[key](arg);
}
export function getGraphUserDataEntry(key, userData) {
    userData = userData === undefined ? wsi4.graph.userData() : userData;
    const value = userData[graphUserDataKeyMap[key]];
    return isGraphUserDataEntryType(key, value) ? value : undefined;
}
export function createUpdatedGraphUserData(key, value, userData) {
    userData = userData === undefined ? wsi4.graph.userData() : userData;
    userData[graphUserDataKeyMap[key]] = value;
    return userData;
}
const isValidNodeUserDataValueFunctionMap = {
    sheetMaterial: (value) => getTable(TableType.sheetMaterial)
        .some(row => row.identifier === value.identifier),
    comment: () => true,
    materialCostsPerPiece: () => true,
    setupTime: () => true,
    unitTimePerPiece: () => true,
    cuttingGas: (arg) => getTable(TableType.laserSheetCuttingGas)
        .some(row => row.identifier === arg.identifier),
    packaging: (arg) => getTable(TableType.packaging)
        .some(row => row.identifier === arg.identifier),
    transportDistance: () => true,
    importId: () => true,
    attachments: () => true,
    testReportRequired: () => true,
    fixedRotations: () => true,
    deburrDoubleSided: () => true,
    numThreads: () => true,
    numCountersinks: () => true,
    sheetTappingData: (arg) => arg.every(entry => isTappingDataEntry(entry)),
    articleUserData: () => true,
    userDefinedScalePrices: () => true,
    tubeMaterial: (value) => value === undefined || getTable(TableType.tubeMaterial)
        .some(row => row.identifier === value.identifier),
    tubeSpecification: (value) => value === undefined || getTable(TableType.tubeSpecification)
        .some(row => row.identifier === value.identifier),
    sheetFilterSheetIds: (value) => {
        const sheets = getTable(TableType.sheet);
        return value.every(entry => sheets.some(s => s.identifier === entry));
    },
    bendLineEngravingMode: () => true,
};
export function isValidNodeUserDataValue(key, value) {
    const f = isValidNodeUserDataValueFunctionMap[key];
    return f(value);
}
